<template>
  <AdminAppLayout title="HRDOS | Unlock Requests">
    
    <div class="row" style="margin-bottom: 20px;">
      <div class="col s12">
        <h4 class="blue-text text-darken-2" style="font-weight: 600;">Pending Unlock Requests</h4>
        <p class="grey-text">Review and manage requests to edit locked employee records.</p>
      </div>
    </div>

    <div class="divider" style="margin-bottom: 25px; border-bottom: 2px dashed #283593;"></div>

    <!-- DESKTOP SKELETON TABLE -->
    <div class="table-responsive-wrapper hide-on-med-and-down" v-show="loader">
      <table class="highlight">
        <thead>
          <tr>
            <th>Date Requested</th>
            <th>LTO ID</th>
            <th>Employee Name</th>
            <th>Reason Provided</th>
            <th class="center-align">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="n in 3" :key="'skeleton-' + n">
            <td><div class="skeleton skeleton-text" style="width: 100px;"></div></td>
            <td><div class="skeleton skeleton-text" style="width: 80px;"></div></td>
            <td><div class="skeleton skeleton-text" style="width: 150px;"></div></td>
            <td><div class="skeleton skeleton-text" style="width: 250px;"></div></td>
            <td class="center-align"><div class="skeleton skeleton-button" style="width: 120px; margin: 0 auto;"></div></td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- DESKTOP DATA TABLE -->
    <div class="table-responsive-wrapper hide-on-med-and-down" v-show="!loader">
      <table class="highlight bordered">
        <thead class="blue lighten-5">
          <tr>
            <th>Date Requested</th>
            <th class="nowrap">LTO ID</th>
            <th>Employee Name</th>
            <th>Reason Provided</th>
            <th class="center-align nowrap">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!requests.length">
            <td colspan="5" class="center-align grey-text" style="padding: 30px 0;">
              <i class="material-icons" style="font-size: 3rem; opacity: 0.5;">check_circle</i>
              <p style="font-size: 1.2rem; margin-top: 10px;">All caught up! No pending unlock requests.</p>
            </td>
          </tr>

          <tr v-else v-for="request in requests" :key="request.id">
            <td class="nowrap"><b>{{ formatDate(request.created_at) }}</b></td>
            <td class="nowrap">{{ request.employee?.employee_id_number || 'N/A' }}</td>
            <td style="font-weight: 500;">
              {{ request.employee?.first_name }} {{ request.employee?.last_name }}
            </td>
            <td>
              <span v-if="request.reason" style="font-style: italic;">"{{ request.reason }}"</span>
              <span v-else class="grey-text">No reason provided</span>
            </td>
            <td class="center-align nowrap">
              <button 
                @click="approveRequest(request.id)" 
                class="btn-small green darken-1" 
                style="border-radius: 4px; margin-right: 8px;"
                :disabled="actionLoader === request.id"
              >
                <b>Approve</b>
              </button>
              <button 
                @click="rejectRequest(request.id)" 
                class="btn-small red darken-1" 
                style="border-radius: 4px;"
                :disabled="actionLoader === request.id"
              >
                <b>Reject</b>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- MOBILE SKELETON CARDS -->
    <div class="row hide-on-large-only" v-show="loader">
      <div v-for="n in 3" :key="'skeleton-mobile-' + n" class="col s12 m6">
        <div class="card" style="border-radius: 8px; box-shadow: none; border: 1px solid #f5f5f5; margin-bottom: 15px;">
          <div class="card-content" style="padding: 20px;">
            <div class="skeleton skeleton-text" style="width: 40%; height: 12px; margin-bottom: 12px;"></div>
            <div class="skeleton skeleton-text" style="width: 80%; height: 20px; margin-bottom: 8px;"></div>
            <div class="skeleton skeleton-text" style="width: 50%; height: 14px; margin-bottom: 15px;"></div>
            <div class="skeleton skeleton-text" style="width: 100%; height: 45px; border-radius: 6px;"></div>
          </div>
          <div class="card-action" style="display: flex; gap: 10px; padding: 15px;">
            <div class="skeleton skeleton-button" style="flex: 1; height: 36px; margin: 0; border-radius: 4px;"></div>
            <div class="skeleton skeleton-button" style="flex: 1; height: 36px; margin: 0; border-radius: 4px;"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- MOBILE DATA CARDS -->
    <div class="row hide-on-large-only" v-show="!loader">
      <div class="col s12" v-if="!requests.length">
        <div class="card-panel center-align z-depth-0" style="padding: 40px 20px; background-color: transparent;">
          <i class="material-icons" style="font-size: 3rem; opacity: 0.5;">check_circle</i>
          <p style="font-size: 1.2rem; margin-top: 10px; color: #9e9e9e;">All caught up! No pending unlock requests.</p>
        </div>
      </div>

      <div v-else v-for="request in requests" :key="'mobile-card-' + request.id" class="col s12 m6">
        <div class="card z-depth-1" style="border-radius: 8px; border: 1px solid #e0e0e0; margin-bottom: 15px;">
          <div class="card-content" style="padding: 20px;">
            <div style="font-size: 0.85rem; color: #9e9e9e; margin-bottom: 8px;">
              <i class="material-icons left" style="font-size: 1rem; margin-right: 5px; line-height: inherit;">access_time</i>
              <b>{{ formatDate(request.created_at) }}</b>
            </div>
            
            <h6 style="margin: 0 0 5px 0; font-weight: 700; font-size: 1.2rem; color: #2c3e50;">
              {{ request.employee?.first_name }} {{ request.employee?.last_name }}
            </h6>
            
            <div style="margin-bottom: 15px; font-size: 0.95rem; color: #546e7a;">
              LTO ID: <b>{{ request.employee?.employee_id_number || 'N/A' }}</b>
            </div>

            <div class="card-panel grey lighten-5 z-depth-0" style="padding: 12px; margin: 0; border-radius: 6px; border: 1px solid #eeeeee;">
              <span v-if="request.reason" style="font-style: italic; font-size: 0.9rem; color: #424242;">"{{ request.reason }}"</span>
              <span v-else class="grey-text" style="font-size: 0.9rem;">No reason provided</span>
            </div>
          </div>
          
          <div class="card-action" style="padding: 15px; border-top: 1px solid #f5f5f5; display: flex; gap: 10px;">
            <button 
              @click="approveRequest(request.id)" 
              class="btn green darken-1" 
              style="flex: 1; border-radius: 4px; padding: 0;"
              :disabled="actionLoader === request.id"
            >
              <b>Approve</b>
            </button>
            <button 
              @click="rejectRequest(request.id)" 
              class="btn red darken-1" 
              style="flex: 1; border-radius: 4px; padding: 0;"
              :disabled="actionLoader === request.id"
            >
              <b>Reject</b>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ACTION MODAL -->
    <div id="action-modal" class="modal centered-modal" ref="actionModal" style="max-width: 450px; border-radius: 8px;">
      <div class="modal-content">
        <h5 :class="selectedAction === 'approve' ? 'green-text' : 'red-text'" style="margin-top: 0; font-weight: 600;">
          {{ selectedAction === 'approve' ? 'Approve Request' : 'Reject Request' }}
        </h5>
        <p style="font-size: 1.1rem; margin-top: 15px;">{{ modalMessage }}</p>
      </div>
      <div class="modal-footer">
        <button 
          @click="closeModal" 
          class="btn-flat" 
          style="border-radius: 4px;"
        >
          Cancel
        </button>
        <button 
          @click="proceedWithAction" 
          class="btn-flat" 
          :class="selectedAction === 'approve' ? 'green-text' : 'red-text'"
          style="border-radius: 4px; font-weight: bold;"
        >
          Yes, {{ selectedAction === 'approve' ? 'Approve' : 'Reject' }}
        </button>
      </div>
    </div>

  </AdminAppLayout>
</template>

<script>
import { api } from "@/axios";
import AdminAppLayout from "../../layout/AdminAppLayout.vue";

export default {
  name: "EmployeeReopenRequestsView",
  components: {
    AdminAppLayout,
  },
  data() {
    return {
      requests: [],
      loader: true,
      actionLoader: null, 
      modalInstance: null,
      selectedRequestId: null,
      selectedAction: null,
      modalMessage: "",
    };
  },
  methods: {
    async fetchRequests() {
      this.loader = true;
      await api.get('/unlock-requests')
        .then(res => {
          this.requests = res.data;
        })
        .catch(err => {
          console.error(err);
          if (typeof window.M !== 'undefined') {
            window.M.toast({html: 'Failed to load requests.', classes: 'red'});
          }
        })
        .finally(() => {
          this.loader = false;
        });
    },

    approveRequest(id) {
      this.selectedRequestId = id;
      this.selectedAction = 'approve';
      this.modalMessage = "Are you sure you want to unlock this employee record?";
      if (this.modalInstance) this.modalInstance.open();
    },

    rejectRequest(id) {
      this.selectedRequestId = id;
      this.selectedAction = 'reject';
      this.modalMessage = "Are you sure you want to reject this request? The record will remain locked.";
      if (this.modalInstance) this.modalInstance.open();
    },

    closeModal() {
      if (this.modalInstance) {
        this.modalInstance.close();
      }
    },

    proceedWithAction() {
      if (this.selectedAction === 'approve') {
        this.executeApprove(this.selectedRequestId);
      } else if (this.selectedAction === 'reject') {
        this.executeReject(this.selectedRequestId);
      }
      this.closeModal(); 
    },

    async executeApprove(id) {
      this.actionLoader = id;
      await api.post(`/unlock-requests/${id}/approve`)
        .then(res => {
          if (typeof window.M !== 'undefined') {
            window.M.toast({html: res.data.message, classes: 'green'});
          }
          this.fetchRequests(); 
        })
        .catch(err => {
          console.error(err);
          if (typeof window.M !== 'undefined') {
            window.M.toast({html: 'Failed to approve request.', classes: 'red'});
          }
        })
        .finally(() => {
          this.actionLoader = null;
        });
    },

    async executeReject(id) {
      this.actionLoader = id;
      await api.post(`/unlock-requests/${id}/reject`)
        .then(res => {
          if (typeof window.M !== 'undefined') {
            window.M.toast({html: res.data.message, classes: 'orange darken-2'});
          }
          this.fetchRequests(); 
        })
        .catch(err => {
          console.error(err);
          if (typeof window.M !== 'undefined') {
            window.M.toast({html: 'Failed to reject request.', classes: 'red'});
          }
        })
        .finally(() => {
          this.actionLoader = null;
        });
    },

    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-PH', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  },
  mounted() {
    this.fetchRequests();
    
    if (typeof window.M !== 'undefined' && this.$refs.actionModal) {
      this.modalInstance = window.M.Modal.init(this.$refs.actionModal, {
        dismissible: true
      });
    }
  },
  beforeUnmount() {
    if (this.modalInstance) {
      this.modalInstance.destroy();
    }
  }
};
</script>

<style scoped>
.table-responsive-wrapper {
  overflow-x: auto;
  width: 100%;
}
.nowrap {
  white-space: nowrap;
}

/* Force the modal to center vertically and horizontally */
.centered-modal {
  top: 50% !important;
  transform: translateY(-50%) !important;
}
</style>